// Typed Settings endpoints — driven by specs/seeds/dashboard-v2-settings/sections.yaml
// (CHG-1224) and consumed by the Settings shell (CHG-1239).
//
// Backend endpoints don't exist yet; this client is spec-driven per
// /tmp/team-v2-scaffold-brief-v6.md ("mock the response shape if endpoint
// doesn't exist yet — it's spec-driven"). When the org-management module
// ships the endpoints, this file requires zero changes.

import { apiClient } from "./client";

/** Nested values payload: { sectionId: { fieldId: value } }. */
export type SettingsValues = Record<string, Record<string, unknown>>;

// CHG-2545 / DRIFT-934 — public password-policy descriptor. Mirrors the
// backend `PasswordPolicy.descriptors()` (services/password_policy.py) and
// the v1 `web/dashboard` `PasswordPolicyDescriptor`. The endpoint
// (GET /settings/password-policy, operation_id getPasswordPolicy) is
// intentionally unauthenticated so pre-session auth pages (reset /
// accept-invite / register-from-email) can align their client-side
// min-length guard with the org-configurable server policy instead of
// hardcoding a now-stale literal.
export interface PasswordPolicyRule {
  code: string;
  description: string;
  severity: "error" | "warning";
}

export interface PasswordPolicyDescriptor {
  min_length: number;
  require_uppercase: boolean;
  require_lowercase: boolean;
  require_digit: boolean;
  require_symbol: boolean;
  forbid_equals_email: boolean;
  forbid_contains_email_localpart: boolean;
  contains_email_localpart_mode: "reject" | "warn";
  rules: PasswordPolicyRule[];
}

export async function getPasswordPolicy(): Promise<PasswordPolicyDescriptor> {
  const r = await apiClient().get<PasswordPolicyDescriptor>("/settings/password-policy");
  return r.data;
}

export async function getAllSettings(orgId: string): Promise<SettingsValues> {
  const r = await apiClient().get<SettingsValues>(`/organizations/${orgId}/settings`);
  return r.data ?? {};
}

export async function patchSection(
  orgId: string,
  sectionId: string,
  values: Record<string, unknown>,
): Promise<void> {
  await apiClient().patch(`/organizations/${orgId}/settings/${sectionId}`, values);
}

export const settingsApi = {
  getAll: getAllSettings,
  patchSection,
  getPasswordPolicy,
};
